/**
 * RUI-05: a "rappel" qualification must carry a dated follow-up, otherwise it
 * never reaches Tâches (FollowupsScreen lists `follow_up_at` only). The agent
 * picks a date, a time and the time zone the time is meant in; the call
 * stores the instant as UTC ISO in `follow_up_at`.
 */

/** Offset of `timeZone` from UTC at `instant`, in minutes (Paris in winter: 60). */
function offsetMinutes(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((asUtc - instant.getTime()) / 60000);
}

/**
 * Wall-clock `date` (YYYY-MM-DD) and `time` (HH:mm) in `timeZone`, as a UTC
 * ISO string. Null when an input is missing or invalid.
 */
export function zonedTimeToUtcIso(date: string, time: string, timeZone: string): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !t || !timeZone) return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
  } catch {
    return null;
  }
  const [y, mo, da, h, mi] = [d[1], d[2], d[3], t[1], t[2]].map(Number) as [number, number, number, number, number];
  if (mo < 1 || mo > 12 || da < 1 || da > 31 || h > 23 || mi > 59) return null;
  const naive = Date.UTC(y, mo - 1, da, h, mi);
  // Two passes settle the offset around a daylight-saving change.
  let guess = naive - offsetMinutes(new Date(naive), timeZone) * 60000;
  guess = naive - offsetMinutes(new Date(guess), timeZone) * 60000;
  const result = new Date(guess);
  return Number.isNaN(result.getTime()) ? null : result.toISOString();
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris';
  } catch {
    return 'Europe/Paris';
  }
}

/** Zones offered first; the browser's own zone is added when missing. */
export const COMMON_TIME_ZONES = [
  'Europe/Paris', 'Europe/London', 'Europe/Brussels', 'Europe/Zurich', 'Europe/Madrid',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'America/Montreal',
  'Africa/Casablanca', 'Indian/Reunion', 'America/Martinique', 'Pacific/Noumea', 'Asia/Dubai',
] as const;

export class CallbackWithoutDateError extends Error {
  readonly code = 'CALLBACK_DATE_REQUIRED';
  constructor() {
    super('A callback qualification needs a follow-up date (follow_up_at).');
    this.name = 'CallbackWithoutDateError';
  }
}

/** Throws when a callback qualification has no valid `follow_up_at`. */
export function assertCallbackDated(data: { call_outcome?: string; follow_up_at?: string } | null | undefined): void {
  if (data?.call_outcome !== 'callback_requested') return;
  const at = data.follow_up_at ? new Date(data.follow_up_at) : null;
  if (!at || Number.isNaN(at.getTime())) throw new CallbackWithoutDateError();
}
